import React, { useEffect, useState } from "react";
import { CabinetsWrapper } from "./styled";
import {
  BackButton,
  Box,
  ButtonStyled,
  HeadingStyled,
  NotAuthorizedForResource,
  ThemeLoader,
} from "../../../../components/styled/Elements";
import { useTranslation } from "react-i18next";
import FloatingInput from "../../../../components/common/inputFields/FloatingInput";
import {
  PlaceholderImg,
  PlusIcon,
  SearchIcon,
} from "../../../../components/styled/AllImages";
import ListTable from "../../../../components/common/tables/ListTable";
import HistoryModal from "../../../../components/admin/Modals/HistoryModal/HistoryModal";
import { Col, Row } from "react-bootstrap";
import CustomTile from "../../../../components/common/statusTiles/CustomTile";
import {
  fileTileArray,
  stepsToTransfer,
} from "../../../../helpers/adminConstants/AdminConstants";
import { useNavigate, useParams } from "react-router-dom";
import TransferModal from "../components/TransferModal";
import Breadcrumbs from "../../../../components/admin/Breadcrumbs/Breadcrumbs";
import DeleteModal from "../../../../components/admin/Modals/DeleteModal/DeleteModal";
import CabinetsApi from "../../../../services/api/cabinetsApi";
import { useDebounce } from "../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import {
  getFormattedDate,
  getFormattedTime,
  getModuleOperations,
  hasPlanFeature,
} from "../../../../utils";
import { useForm } from "react-hook-form";
import { FilesColumns } from "../../../../helpers/adminConstants/TableConstants";
import { useUser } from "../../../../context/userContext";
import RequestModal from "../../../../components/admin/Modals/RequestModal/RequestModal";
import RequestsApi from "../../../../services/api/requestsApi";

const Files = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = Number(user?.id);
  const userTypeId = Number(user?.user_type_id);
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [filesDataList, setFilesDataList] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [currentOwner, setCurrentOwner] = useState(null);
  const [parentInfo, setParentInfo] = useState(null);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [stats, setStats] = useState(fileTileArray);
  const [cabinetFileId, setCabinetFileId] = useState();
  const [fileHistory, setFileHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [transferSelections, setTransferSelections] = useState(
    Array(stepsToTransfer.length).fill(null)
  );
  const [isTemporary, setIsTemporary] = useState(null);
  const ASSET_URL = process.env.REACT_APP_ASSET_URL;
  const filteredColumns =
    parseInt(user?.user_type_id) === 6
      ? FilesColumns.filter((col) => col.name !== "privacy")
      : FilesColumns;

  const handleEdit = (row) => {
    navigate(`/cabinets/${id}/files/edit-file/${row.hash_id}`);
  };
  const handleInfo = (row) => {
    setShowHistoryModal(true);
    getFileHistory(row.hash_id);
  };

  const onDelete = (row) => {
    setCabinetFileId(row.hash_id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      const response = await CabinetsApi.deleteCabinetFile(id, cabinetFileId);
      if (response.code === 200) {
        toast.success(t("file_deleted"));
        setShowDeleteModal(false);
        setCabinetFileId(null);
        getFilesData(id, activePage, debouncedSearchQuery);
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {}
  };

  const handleView = (row) => {
  setCabinetFileId(row.id);

  const {
    current_owner,
    current_owner_department_id,
    user_have_child_access,
    cabinetfile_details,
    cabinet_name,
    access_granted_requests,
  } = row || {};

  const { department } = user || {};
  const departmentId = Number(department?.id);

  // 1. Basic Ownership Check
  const isLatestOwner = current_owner && userId === Number(current_owner);

  // 2. Access via Detail Records (Unified Logic)
  const isOpenToUser = (cabinetfile_details || []).some((detail) => {
    let targetDetails = [detail];

    // Check if we need to redirect to the Parent Cabinet's settings
    if (detail?.group_type === "base_cabinet" && cabinet_name?.cabinet_details) {
      targetDetails = cabinet_name.cabinet_details;
    }

    // Check if any of the applicable details grant access
    return targetDetails.some((item) => {
      // Normalize setting ID: cabinetfile_details uses common_settings_id, 
      // cabinet_details uses setting_id
      const rawSettingId = item?.common_settings_id || item?.setting_id;
      
      const sId = rawSettingId !== 1 
        ? rawSettingId 
        : cabinet_name?.setting_id;

      // Check User selection
      const isUserInDetail = item?.selected_users?.includes(userId);

      // Check Department selection
      const selectedDepts = item?.selected_departments;
      const isDeptInDetail = Array.isArray(selectedDepts)
        ? selectedDepts.includes(departmentId)
        : (selectedDepts != null && Number(selectedDepts) === departmentId);

      // Permission Rules
      return (
        (sId === 3 && Number(current_owner_department_id) === departmentId) ||
        ([4, 5, 9].includes(sId) && isUserInDetail) ||
        ([6, 7].includes(sId) && isDeptInDetail)
      );
    });
  });

  // 3. Request Access Check
  const hasAccessThroughRequest = Array.isArray(access_granted_requests) &&
    access_granted_requests.some((item) => Number(item?.from_user_id) === userId);

  console.log('userId: ', userId, 'isLatestOwner: ', isLatestOwner, 'isOpenToUser: ', isOpenToUser, 'hasAccessThroughRequest: ', hasAccessThroughRequest, 'user_have_child_access: ', user_have_child_access, "userTypeId: ", userTypeId);

  // 4. Final Permission Check
  if (
    isLatestOwner ||
    isOpenToUser ||
    hasAccessThroughRequest ||
    user_have_child_access ||
    userTypeId === 6
  ) {
    navigate(`/cabinets/${id}/files/${row?.hash_id}/archives`);
  } else {
    if (hasPlanFeature(4)) {
      setShowRequestModal(true);
    } else {
      toast.error(t("no_access_to_open_or_request"));
    }
  }
};
  const handleRequest = async () => {
    try {
      setRequestLoading(true);
      const { code, message } = await RequestsApi.createRequest({
        module_id: 2,
        module_reference_id: cabinetFileId,
      });
      if (code === 200) {
        toast.success(t("req_sent_successful"));
        setShowRequestModal(false);
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setRequestLoading(false);
    }
  };

  const handleTransfer = (row) => {
    setShowTransferModal(true);
    setCabinetFileId(row.id);
  };
  const handleStatusToggle = async (row) => {
    const newObj = {
      file_id: row.id,
      _method: "PUT",
      status: parseInt(row.status) === 1 ? 0 : 1,
    };
    try {
      const response = await CabinetsApi.statusCabinetFile(id, newObj);
      if (response.code === 200) {
        toast.success(
          `${t("file")} ${t("status")} ${t("Updated_Successfully")}`
        );
        getFilesData(id, activePage, debouncedSearchQuery);
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  const getFilesData = async (id, page = 1, query = "") => {
    try {
      setAccessDenied(false);
      setLoading(true);
      const response = await CabinetsApi.cabinteFilesList(id, page, query);

      if (response?.data) {
        setIsTemporary(response?.data?.parent_is_temporary);
        const updatedStats = fileTileArray.map((tile) => {
          switch (tile.title) {
            case "ActiveFiles":
              return {
                ...tile,
                count: response?.data?.active_files || tile.count,
              };
            case "InactiveFile":
              return {
                ...tile,
                count: response?.data?.inactive_files || tile.count,
              };
            case "TotalFiles":
              return {
                ...tile,
                count: response?.data?.total_files || tile.count,
              };
            default:
              return tile;
          }
        });
        setStats(updatedStats);

        const filesDataListing = response?.data?.files?.data || [];

        const updatedFilesDataList = filesDataListing.map((list) => ({
          ...list,
          id: list?.id,
          // filename: list?.name,
          // archivesnumber: list?.no_of_archives,
          description: list?.description || "",
          status: list?.status,
          profile: list?.owner_user?.profile
            ? `${ASSET_URL}/${list?.owner_user?.profile}`
            : PlaceholderImg,
          owner_user: {
            ...list?.owner_user,
            name:
              parseInt(user?.id) === parseInt(list?.owner_user?.id)
                ? t("myself")
                : list?.owner_user?.name,
            subLabel:
              parseInt(user?.id) !== parseInt(list?.owner_user?.id) &&
              list?.owner_user?.user_detail?.department_selective &&
              list?.owner_user?.user_detail?.office_location_selective
                ? language === "en"
                  ? `${list?.owner_user?.user_detail?.department_selective?.name_en} (${list?.owner_user?.user_detail?.office_location_selective?.name_en})`
                  : `${list?.owner_user?.user_detail?.department_selective?.name_ar} (${list?.owner_user?.user_detail?.office_location_selective?.name_ar})`
                : "",
          },
        }));
        console.log(updatedFilesDataList);

        setCurrentOwner(response?.data?.cabinet_owner);
        setParentInfo(response?.data?.parent_info);
        setFilesDataList(updatedFilesDataList);
        setTotalCount(response?.data?.files?.total);
        setPerPage(response?.data?.files?.per_page);
      } else {
        setCurrentOwner(null);
        setFilesDataList([]);
        setTotalCount(null);
        setPerPage(null);
      }
      if (response?.code === 403) {
        setAccessDenied(true);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handelAdd = () => {
    navigate(`/cabinets/${id}/files/add-file`);
  };
  const handleSearch = (e) => {
    const query = e.target.value.trim();
    setSearchQuery(query); // Update the search query state
  };
  useEffect(() => {
    getFilesData(id, activePage, debouncedSearchQuery);
  }, [id, activePage, debouncedSearchQuery]);

  const getFileHistory = async (fileId) => {
    try {
      setHistoryLoading(true);

      const response = await CabinetsApi.getFileHistory(id, fileId);

      if (response?.data) {
        const createdBy = {
          id: response?.data?.created_by_user?.id,
          name: response?.data?.created_by_user?.name,
          subLabel: response?.data?.created_by_user?.user_detail
            ? `${
                language === "en"
                  ? response?.data?.created_by_user?.user_detail
                      ?.department_selective?.name_en
                  : response?.data?.created_by_user?.user_detail
                      ?.department_selective?.name_ar
              } (${
                language === "en"
                  ? response?.data?.created_by_user?.user_detail
                      ?.office_location_selective?.name_en
                  : response?.data?.created_by_user?.user_detail
                      ?.office_location_selective?.name_ar
              })`
            : "",
          avatar: response?.data?.created_by_user?.profile
            ? `${ASSET_URL}/${response?.data?.created_by_user?.profile}`
            : PlaceholderImg,
          date: getFormattedDate(response?.data?.created_at),
          time: getFormattedTime(response?.data?.created_at),
        };

        const editHistory =
          response?.data?.history_update?.length > 0 &&
          response?.data?.history_update?.map((user) => ({
            id: user?.updated_by?.id,
            name: user?.updated_by?.name,
            subLabel: user?.updated_by?.user_detail
              ? `${
                  language === "en"
                    ? user?.updated_by?.user_detail?.department_selective
                        ?.name_en
                    : user?.updated_by?.user_detail?.department_selective
                        ?.name_ar
                } (${
                  language === "en"
                    ? user?.updated_by?.user_detail?.office_location_selective
                        ?.name_en
                    : user?.updated_by?.user_detail?.office_location_selective
                        ?.name_ar
                })`
              : "",
            avatar: user?.updated_by?.profile
              ? `${ASSET_URL}/${user?.updated_by?.profile}`
              : PlaceholderImg,
            date: getFormattedDate(user.created_at),
            time: getFormattedTime(user.created_at),
          }));

        const transferHistory =
          response?.data?.transfer_histories.length > 0 &&
          response?.data?.transfer_histories.map((transUser) => ({
            from: {
              id: transUser?.transfer_by?.id,
              name: transUser?.transfer_by?.name,
              subLabel: transUser.transfer_by?.user_detail
                ? `${
                    language === "en"
                      ? transUser.transfer_by?.user_detail?.department_selective
                          ?.name_en
                      : transUser.transfer_by?.user_detail?.department_selective
                          ?.name_ar
                  } (${
                    language === "en"
                      ? transUser?.transfer_by?.user_detail
                          ?.office_location_selective?.name_en
                      : transUser?.transfer_by?.user_detail
                          ?.office_location_selective?.name_ar
                  })`
                : "",
              avatar: transUser?.transfer_by?.profile
                ? `${ASSET_URL}/${transUser?.transfer_by?.profile}`
                : PlaceholderImg,
            },
            to: {
              id: transUser?.transfer_to?.id,
              name: transUser?.transfer_to?.name,
              subLabel: transUser.transfer_to?.user_detail
                ? `${
                    language === "en"
                      ? transUser.transfer_to?.user_detail?.department_selective
                          ?.name_en
                      : transUser.transfer_to?.user_detail?.department_selective
                          ?.name_ar
                  } (${
                    language === "en"
                      ? transUser?.transfer_to?.user_detail
                          ?.office_location_selective?.name_en
                      : transUser?.transfer_to?.user_detail
                          ?.office_location_selective?.name_ar
                  })`
                : "",
              avatar: transUser?.transfer_to?.profile
                ? `${ASSET_URL}/${transUser?.transfer_to?.profile}`
                : PlaceholderImg,
            },
            date: getFormattedDate(transUser.created_at),
            time: getFormattedTime(transUser.created_at),
          }));

        setFileHistory((prevData) => ({
          ...prevData,
          createdBy: createdBy,
          editHistory: editHistory,
          transferHistory: transferHistory,
        }));
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    const selectedRadio = document.querySelector(
      'input[name="trans_user_select"]:checked'
    );
    const selectedId = selectedRadio ? parseInt(selectedRadio.value, 10) : null;

    const updatedData = {
      ...formData,
      action: "create",
      file_id: cabinetFileId,
      sent_to: selectedId,
    };
    try {
      setTransferLoading(true);
      const response = await CabinetsApi.transferCabinetFile(id, updatedData);
      if (response.code === 200) {
        toast.success(`${t("file")} ${t("Transfer")} ${t("Successfully")}`);
        reset();
        setShowTransferModal(false);
        setCurrentStep(0);
        setTransferSelections(Array(stepsToTransfer.length).fill(null));
        getFilesData(activePage);
        // navigate(`/cabinet/${id}/files`)
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <CabinetsWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("files")}
            onClick={() => navigate("/cabinets")}
          />
          <Breadcrumbs />
        </Box>
        <Box className="d-flex gap-2 head-item-two">
          <FloatingInput
            name="search"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("searchFile")}
            icon={SearchIcon}
            onChange={handleSearch} // Call the search handler
          />
          {getModuleOperations(2, 2) &&
            currentOwner &&
            userId === Number(currentOwner?.id) && (
              <ButtonStyled
                label={t("add_file")}
                icon={PlusIcon}
                onClick={handelAdd}
              />
            )}
        </Box>
      </Box>
      {loading ? (
        <ThemeLoader />
      ) : accessDenied ? (
        <NotAuthorizedForResource />
      ) : (
        <>
          <Box className="px-4 mt-4">
            <HeadingStyled color="var(--text)">
              {parentInfo?.name} {t("Cabinet")}
            </HeadingStyled>
          </Box>
          <Box className="content-container">
            <Row className="justify-content-center">
              {stats.map((tile, key) => (
                <Col lg={3} md={6} sm={12} className="my-2">
                  <CustomTile key={key} {...tile} />
                </Col>
              ))}
            </Row>
          </Box>
          <Box>
            <ListTable
              options={["open", "info", "edit", "transfer", "status", "delete"]}
              typeId={2}
              columns={filteredColumns}
              rows={filesDataList}
              onEdit={handleEdit}
              onDelete={onDelete}
              onOpen={handleView}
              onInfoView={handleInfo}
              onTransfer={handleTransfer}
              onStatusToggle={handleStatusToggle}
              totalCount={totalCount}
              perPage={perPage}
              activePage={activePage}
              setActivePage={setActivePage}
            />
          </Box>
          <HistoryModal
            title={t("FileHistory")}
            show={showHistoryModal}
            onHide={() => setShowHistoryModal(false)}
            createdBy={fileHistory.createdBy}
            editHistory={fileHistory.editHistory}
            transferHistory={fileHistory.transferHistory}
            historyLoading={historyLoading}
          />

          <TransferModal
            onSubmit={onSubmit}
            show={showTransferModal}
            onHide={() => setShowTransferModal(false)}
            transferLoading={transferLoading}
            selections={transferSelections}
            setSelections={setTransferSelections}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
          <DeleteModal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            onDelete={handleDelete}
            title={t("File")}
          />
          <RequestModal
            title={"File"}
            show={showRequestModal}
            onHide={() => setShowRequestModal(false)}
            handleRequest={handleRequest}
            isLoading={requestLoading}
          />
        </>
      )}
    </CabinetsWrapper>
  );
};

export default Files;
